import { useMemo, useState } from 'react';
import {
  Button,
  Group,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useNavigate } from 'react-router';
import { useOlayAc, useOlayOptions, useSahaGunu } from '../api/queries';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { gunGirdisiIso } from '../lib/format';

/**
 * KAPI 1 — oturumlu olay bildirimi + uzmanın KAZA kaydı (F3B·S2, U5).
 *
 * - Çalışan TEK kutu görür (R2): üç türden birini seçer, anlatır, gönderir —
 *   arkada ne olacağını bilmesi gerekmez (akış ayrımı uzmanda).
 * - "Kaza (tür boş)" seçeneği YALNIZ `olay_inceleme` üyelerine görünür
 *   (`options.kazaAcabilir` — karar sunucuda, ekran taşıyıcı; O10).
 * - ANONİM SEÇENEĞİ YOK (✏️ madde 64, F3B·S4 — U5 revizyonu): girişliyken
 *   anonim bildirim yoktur; anonim bildirim TEK KAPI — QR/link ile açılan
 *   oturumsuz public yüzey. Uç da anonim gövdesini 422 ile reddeder; buraya
 *   anonim anahtarı GERİ EKLEME.
 * - İşyeri/lokasyon havuzu bahçeden gelir (options — B3 fail-closed).
 */
export function OlayKayitScreen(): React.JSX.Element {
  const options = useOlayOptions();
  const ac = useOlayAc();
  const navigate = useNavigate();

  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [locationNodeId, setLocationNodeId] = useState<string | null>(null);
  const [turId, setTurId] = useState<string | null>(null);
  const [kazaMi, setKazaMi] = useState(false);
  const [tarih, setTarih] = useState('');
  // Madde 302: takvimin üst sınırı ("bugün") SAHA GÜNÜ (sunucudan).
  const sahaGunu = useSahaGunu();
  const [anlati, setAnlati] = useState('');

  // §2.40 — TARİH GEÇERLİLİĞİ GÖNDERİM ÖNCESİ ÇÖZÜLÜR. Eskiden ISO damgası
  // `onClick` içinde kuruluyordu; geçersiz girdide `toISOString()` atıyor,
  // istek hiç kurulmuyor ve düğme SESSİZ kalıyordu.
  const tarihIso = gunGirdisiIso(tarih);
  const tarihHatasi = tarih.trim() !== '' && tarihIso === null;
  const hazir =
    Boolean(workplaceId) && (kazaMi || Boolean(turId)) && tarihIso !== null && anlati.trim() !== '';

  const turSecenekleri = useMemo(
    () => (options.data?.olayTurleri ?? []).map((t) => ({ value: t.id, label: t.ad })),
    [options.data],
  );
  // A1 — lokasyon bildirimde de sorulabilir (isteğe bağlı; ağacı olmayan
  // işyerinde alan hiç çizilmez). Havuz options'tan, işyerine göre süzülür.
  const lokasyonSecenekleri = useMemo(
    () =>
      (options.data?.lokasyonlar ?? [])
        .filter((l) => l.workplaceId === workplaceId)
        .map((l) => ({ value: l.id, label: l.ad })),
    [options.data, workplaceId],
  );

  return (
    <Stack maw={640}>
      <Title order={3}>Yeni olay bildirimi</Title>
      <QueryStatus queries={[options]} />
      <Select
        label="İşyeri"
        required
        searchable
        data-testid="olay-isyeri"
        data={(options.data?.workplaces ?? []).map((w) => ({
          value: w.id,
          label: `${w.ad} (${w.companyAd})`,
        }))}
        value={workplaceId}
        onChange={(deger) => {
          setWorkplaceId(deger);
          setLocationNodeId(null);
        }}
      />
      {lokasyonSecenekleri.length > 0 && (
        <Select
          label="Lokasyon (isteğe bağlı)"
          clearable
          searchable
          data-testid="olay-lokasyon"
          data={lokasyonSecenekleri}
          value={locationNodeId}
          onChange={setLocationNodeId}
        />
      )}
      {options.data?.kazaAcabilir && (
        <Switch
          label="Kaza kaydı (uzman — tür seçilmez, araştırma akışına düşer)"
          data-testid="olay-kaza"
          checked={kazaMi}
          onChange={(event) => {
            setKazaMi(event.currentTarget.checked);
            if (event.currentTarget.checked) setTurId(null);
          }}
        />
      )}
      {!kazaMi && (
        <Select
          label="Olay türü"
          required
          data-testid="olay-tur"
          data={turSecenekleri}
          value={turId}
          onChange={setTurId}
        />
      )}
      {/* Takvim seçici (native `type="date"`): geçersiz metin GİRİLEMEZ.
          Kutuya elle yazılabilen tarayıcılar için doğrulama yine de burada —
          "kontrol ya çalışır ya kapalıdır" (§2.40). */}
      <TextInput
        type="date"
        label="Olay tarihi"
        required
        max={sahaGunu.data}
        data-testid="olay-tarih"
        value={tarih}
        error={tarihHatasi ? 'Geçerli bir tarih seçin (gün.ay.yıl).' : null}
        onChange={(event) => setTarih(event.currentTarget.value)}
      />
      <Textarea
        label="Anlatı"
        required
        minRows={4}
        data-testid="olay-anlati"
        value={anlati}
        onChange={(event) => setAnlati(event.currentTarget.value)}
      />
      {ac.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="olay-ac-hata">
          {ac.error.message}
        </Text>
      )}
      <Group>
        <Button
          data-testid="olay-ac-onayla"
          disabled={!hazir}
          loading={ac.isPending}
          onClick={() => {
            if (!hazir || !workplaceId || tarihIso === null) return;
            ac.mutate(
              {
                workplaceId,
                locationNodeId,
                olayTuruRefId: kazaMi ? null : turId,
                olayTarihi: tarihIso,
                anlati: anlati.trim(),
              },
              {
                // U8 — TÜR KAPIYI SEÇER: sunucu kaydı hangi kaba koyduğunu
                // `kap` ile söyler; ekran YENİDEN türetmez ve kullanıcıya
                // *"olay mı uygunsuzluk mu"* diye SORMAZ (§24.1/8).
                onSuccess: (sonuc) =>
                  void navigate(
                    sonuc.kap === 'uygunsuzluk'
                      ? `/uygunsuzluklar/${sonuc.uygunsuzluk!.id}`
                      : `/olaylar/${sonuc.olay!.id}`,
                  ),
              },
            );
          }}
        >
          Bildir
        </Button>
        <Button variant="default" onClick={() => void navigate('/olaylar')}>
          Vazgeç
        </Button>
      </Group>
    </Stack>
  );
}
